import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../../../../utils/api/deliveryDocumentation";
import { Button, Checkbox, Input, XCircle } from "../../../ui";
import { useAppLabel } from "../../../../utils/useAppLabel";

const EMAIL = /^[^\s@.](?!.*\.\.)[^\s@]*@[^\s@.](?:[^\s@]*[^\s@.])?\.[^\s@.]+$/;
const EMPTY_SETTINGS = Object.freeze({
  to: [],
  cc: [],
  subjectTemplate: "",
  bodyTemplate: "",
  variables: [],
});

function normalizeEmail(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function splitRecipients(value) {
  return String(value || "")
    .split(/[;,\n]+/)
    .map(normalizeEmail)
    .filter(Boolean);
}

function unique(values) {
  return [...new Set(values)];
}

function normalizeRecipients(toValue, ccValue) {
  const to = unique(splitRecipients(toValue));
  const toSet = new Set(to);
  const cc = unique(splitRecipients(ccValue)).filter(
    (email) => !toSet.has(email),
  );
  return { to, cc };
}

function settingsToForm(settings = {}) {
  const safeSettings = settings || EMPTY_SETTINGS;
  return {
    to: (safeSettings.to || []).join("\n"),
    cc: (safeSettings.cc || []).join("\n"),
    subjectTemplate: safeSettings.subjectTemplate || "",
    bodyTemplate: safeSettings.bodyTemplate || "",
    variables: safeSettings.variables || [],
  };
}

function sameForm(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function renderTemplate(template, context, variables) {
  const allowed = new Set(variables.map((item) => item.key));
  return String(template || "").replace(
    /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g,
    (match, name) => (allowed.has(name) ? String(context[name] ?? "") : match),
  );
}

function validateForm(form, availableVariables) {
  const errors = {};
  const recipients = normalizeRecipients(form.to, form.cc);
  const invalidTo = recipients.to.filter((email) => !EMAIL.test(email));
  const invalidCc = recipients.cc.filter((email) => !EMAIL.test(email));
  if (!recipients.to.length) errors.to = "البريد الرئيسي مطلوب.";
  else if (invalidTo.length)
    errors.to = `صيغة بريد غير صحيحة: ${invalidTo.join("، ")}`;
  if (invalidCc.length)
    errors.cc = `صيغة بريد غير صحيحة: ${invalidCc.join("، ")}`;
  if (!form.subjectTemplate.trim())
    errors.subjectTemplate = "عنوان الاعتراض مطلوب.";
  if (/[\r\n]/.test(form.subjectTemplate))
    errors.subjectTemplate = "العنوان لا يقبل أسطر جديدة.";
  if (!form.bodyTemplate.trim()) errors.bodyTemplate = "نص الاعتراض مطلوب.";
  const allowed = new Set(availableVariables.map((item) => item.key));
  for (const field of ["subjectTemplate", "bodyTemplate"]) {
    const found = [
      ...String(form[field] || "").matchAll(
        /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g,
      ),
    ].map((match) => match[1]);
    const unsupported = unique(found.filter((name) => !allowed.has(name)));
    if (unsupported.length)
      errors[field] =
        `متغير غير مدعوم: ${unsupported.map((name) => `{{${name}}}`).join("، ")}`;
  }
  return { errors, recipients };
}

function RecipientField({ label, value, error, onChange }) {
  const [draft, setDraft] = useState("");
  const values = splitRecipients(value);
  function add() {
    const next = unique([...values, ...splitRecipients(draft)]);
    onChange(next.join("\n"));
    setDraft("");
  }
  function remove(email) {
    onChange(values.filter((item) => item !== email).join("\n"));
  }
  return (
    <div className="setting-field">
      <label>{label}</label>
      <div className={`email-chip-wrap ${error ? "has-error" : ""}`}>
        {values.map((email) => (
          <span
            className={`email-chip ${EMAIL.test(email) ? "" : "invalid"}`}
            key={email}
          >
            {email}
            <Button
              variant="danger"
              appearance="ghost"
              size="sm"
              type="button"
              onClick={() => remove(email)}
              aria-label={`حذف ${email}`}
              icon={<XCircle size={13} />}
            />
          </span>
        ))}
        <Input
          className="email-chip-input"
          fullWidth={false}
          value={draft}
          onBlur={add}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (["Enter", ",", ";"].includes(event.key)) {
              event.preventDefault();
              add();
            }
          }}
          placeholder="اكتب البريد واضغط Enter"
        />
      </div>
      {error && <small className="field-error">{error}</small>}
    </div>
  );
}

export default function DisputeSettings() {
  const appLabel = useAppLabel();
  const [state, setState] = useState({
    loading: true,
    error: "",
    items: [],
    availableVariables: [],
    previewContext: {},
  });
  const [selectedKey, setSelectedKey] = useState("");
  const [form, setForm] = useState(settingsToForm());
  const [savedForm, setSavedForm] = useState(settingsToForm());
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [saving, setSaving] = useState(false);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const lastEditor = useRef("bodyTemplate");

  const load = useCallback(async () => {
    setState((value) => ({ ...value, loading: true, error: "" }));
    try {
      const data = await api.getAdminDisputeSettings();
      const items = data.items || [];
      setState({
        loading: false,
        error: "",
        items,
        availableVariables: data.availableVariables || [],
        previewContext: data.previewContext || {},
      });
      const firstKey = selectedKey || items[0]?.applicationKey || "";
      setSelectedKey(firstKey);
      const next = settingsToForm(
        items.find((item) => item.applicationKey === firstKey)?.settings,
      );
      setForm(next);
      setSavedForm(next);
    } catch (error) {
      setState((value) => ({ ...value, loading: false, error: error.message }));
    }
  }, [selectedKey]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = state.items.find(
    (item) => item.applicationKey === selectedKey,
  );
  const dirty = !sameForm(form, savedForm);
  const preview = useMemo(
    () => ({
      subject: renderTemplate(
        form.subjectTemplate,
        state.previewContext,
        state.availableVariables,
      ),
      body: renderTemplate(
        form.bodyTemplate,
        state.previewContext,
        state.availableVariables,
      ),
    }),
    [form, state.previewContext, state.availableVariables],
  );

  function selectApplication(applicationKey) {
    if (applicationKey === selectedKey) return;
    if (dirty) {
      setFeedback({
        type: "error",
        message: "لديك تعديلات غير محفوظة. احفظها أو ألغِها قبل تغيير التطبيق.",
      });
      return;
    }
    const next = settingsToForm(
      state.items.find((item) => item.applicationKey === applicationKey)
        ?.settings,
    );
    setSelectedKey(applicationKey);
    setForm(next);
    setSavedForm(next);
    setErrors({});
    setFeedback({ type: "", message: "" });
  }

  function variableConfig(key) {
    return form.variables.find((item) => item.key === key);
  }

  function toggleVariable(variable) {
    setForm((value) => {
      const exists = value.variables.some((item) => item.key === variable.key);
      return {
        ...value,
        variables: exists
          ? value.variables.filter((item) => item.key !== variable.key)
          : [
              ...value.variables,
              { ...variable, required: false, fallback: "" },
            ],
      };
    });
  }

  function updateVariable(key, patch) {
    setForm((value) => ({
      ...value,
      variables: value.variables.map((item) =>
        item.key === key ? { ...item, ...patch } : item,
      ),
    }));
  }

  function insertPlaceholder(name) {
    const key = lastEditor.current;
    const ref =
      key === "subjectTemplate" ? subjectRef.current : bodyRef.current;
    const token = `{{${name}}}`;
    const start = ref?.selectionStart ?? form[key].length;
    const end = ref?.selectionEnd ?? form[key].length;
    const nextValue = `${form[key].slice(0, start)}${token}${form[key].slice(end)}`;
    setForm((value) => ({ ...value, [key]: nextValue }));
    window.setTimeout(() => {
      ref?.focus();
      ref?.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  }

  async function save() {
    const validation = validateForm(form, state.availableVariables);
    setErrors(validation.errors);
    setFeedback({ type: "", message: "" });
    if (Object.keys(validation.errors).length) return;
    setSaving(true);
    try {
      const data = await api.updateAdminDisputeSettings(selectedKey, {
        ...validation.recipients,
        subjectTemplate: form.subjectTemplate,
        bodyTemplate: form.bodyTemplate,
        variables: form.variables,
        previewContext: state.previewContext,
      });
      const normalized = settingsToForm(data.item?.settings);
      setForm(normalized);
      setSavedForm(normalized);
      setState((value) => ({
        ...value,
        items: value.items.map((item) =>
          item.applicationKey === selectedKey
            ? {
                ...item,
                configured: true,
                settings: data.item?.settings || EMPTY_SETTINGS,
              }
            : item,
        ),
      }));
      setFeedback({
        type: "success",
        message: "تم حفظ إعدادات الاعتراض لهذا التطبيق.",
      });
    } catch (error) {
      const nextErrors = {};
      if (
        [
          "INVALID_TEMPLATE_PLACEHOLDERS",
          "UNSUPPORTED_TEMPLATE_VARIABLE",
        ].includes(error.code)
      )
        nextErrors.subjectTemplate = `متغير غير مدعوم: ${(error.unsupported || error.variables || []).join("، ")}`;
      if (error.code === "INVALID_RECIPIENTS") nextErrors.to = error.message;
      setErrors(nextErrors);
      setFeedback({ type: "error", message: error.message });
    } finally {
      setSaving(false);
    }
  }

  if (state.loading)
    return <div className="admin-empty">جاري تحميل إعدادات الاعتراض...</div>;
  if (state.error)
    return <div className="admin-note danger">{state.error}</div>;
  if (!state.items.length)
    return (
      <div className="admin-empty">لا توجد تطبيقات توصيل مدعومة حاليًا.</div>
    );

  return (
    <div className="dispute-settings">
      <div className="settings-intro">
        اضبط مستلمي وقوالب الاعتراض لكل تطبيق من مصدر التطبيقات الحالي. لا يتم
        الحفظ إلا عند الضغط على زر الحفظ.
      </div>
      <section className="admin-filter-block dispute-settings-shell">
        <div className="admin-panel-title">
          <h2>إعدادات بريد الاعتراض</h2>
          <span>{dirty ? "تعديلات غير محفوظة" : "آخر نسخة محفوظة"}</span>
        </div>
        <div className="dispute-app-selector">
          {state.items.map((item) => (
            <button
              key={item.applicationKey}
              type="button"
              className={item.applicationKey === selectedKey ? "on" : ""}
              onClick={() => selectApplication(item.applicationKey)}
            >
              <b>{appLabel(item.applicationKey)}</b>
              <span>{item.configured ? "محفوظ" : "غير مكوّن"}</span>
            </button>
          ))}
        </div>
        <div className="dispute-setting-card">
          <div className="dispute-setting-head">
            <b>{appLabel(selected?.applicationKey)}</b>
            <span>
              {selected?.settings?.updatedAt
                ? new Date(selected.settings.updatedAt).toLocaleString("ar-SA")
                : "إعدادات جديدة"}
            </span>
          </div>
          <div className="setting-grid">
            <RecipientField
              label="البريد الرئيسي (To)"
              value={form.to}
              error={errors.to}
              onChange={(to) => setForm((value) => ({ ...value, to }))}
            />
            <RecipientField
              label="نسخة إلى (CC)"
              value={form.cc}
              error={errors.cc}
              onChange={(cc) => setForm((value) => ({ ...value, cc }))}
            />
            <div className="setting-field full">
              <label>قالب عنوان الاعتراض</label>
              <Input
                ref={subjectRef}
                value={form.subjectTemplate}
                onFocus={() => {
                  lastEditor.current = "subjectTemplate";
                }}
                onChange={(event) =>
                  setForm((value) => ({
                    ...value,
                    subjectTemplate: event.target.value,
                  }))
                }
              />
              {errors.subjectTemplate && (
                <small className="field-error">{errors.subjectTemplate}</small>
              )}
            </div>
            <div className="setting-field full">
              <label>قالب نص الاعتراض</label>
              <Input
                textarea
                ref={bodyRef}
                value={form.bodyTemplate}
                onFocus={() => {
                  lastEditor.current = "bodyTemplate";
                }}
                onChange={(event) =>
                  setForm((value) => ({
                    ...value,
                    bodyTemplate: event.target.value,
                  }))
                }
              />
              {errors.bodyTemplate && (
                <small className="field-error">{errors.bodyTemplate}</small>
              )}
            </div>
          </div>
          <div className="template-help variable-manager">
            {state.availableVariables.map((variable) => {
              const enabled = variableConfig(variable.key);
              return (
                <div
                  className={`variable-row ${enabled ? "enabled" : ""}`}
                  key={variable.key}
                >
                  <Button
                    variant="neutral"
                    appearance="outline"
                    size="sm"
                    type="button"
                    onClick={() => insertPlaceholder(variable.key)}
                    title={variable.labelEn}
                  >{`{{${variable.key}}}`}</Button>
                  <span>{variable.labelAr}</span>
                  <small>{variable.sampleValue}</small>
                  <Checkbox
                    checked={Boolean(enabled)}
                    onChange={() => toggleVariable(variable)}
                    label="تفعيل"
                  />
                  {enabled && (
                    <>
                      <Checkbox
                        checked={Boolean(enabled.required)}
                        onChange={(event) =>
                          updateVariable(variable.key, {
                            required: event.target.checked,
                          })
                        }
                        label="مطلوب"
                      />
                      <Input
                        value={enabled.fallback || ""}
                        onChange={(event) =>
                          updateVariable(variable.key, {
                            fallback: event.target.value,
                          })
                        }
                        placeholder="قيمة بديلة"
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="dispute-preview">
            <div>
              <span>معاينة العنوان</span>
              <b>{preview.subject || "—"}</b>
            </div>
            <div>
              <span>معاينة النص</span>
              <pre>{preview.body || "—"}</pre>
            </div>
          </div>
          <div className="admin-actions">
            <Button
              variant="primary"
              appearance="filled"
              type="button"
              loading={saving}
              disabled={saving}
              onClick={save}
            >
              حفظ إعدادات التطبيق
            </Button>
            <Button
              variant="danger"
              appearance="soft"
              type="button"
              disabled={!dirty || saving}
              onClick={() => {
                setForm(savedForm);
                setErrors({});
                setFeedback({ type: "", message: "" });
              }}
            >
              إلغاء التعديلات
            </Button>
            {feedback.message && (
              <div className={`admin-export-feedback ${feedback.type}`}>
                {feedback.message}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
